/**
 * Flow Agent - Generates interactive flowcharts using runAgent edge function
 * Uses SambaNova API via Supabase Edge Function
 */

import { supabase } from '@/integrations/supabase/client';

const ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImFyaWtodG96dmt2eGxyYWl1ZW5oIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA1MTUzNDYsImV4cCI6MjA2NjA5MTM0Nn0.dlbYO0MDYeUENh9tYSJb7ctfxGFEhyahDJ4Ia00WCNU';
const SUPABASE_URL = 'https://arikhtozvkvxlraiuenh.supabase.co';

interface FlowNode {
  id: string;
  type: 'start' | 'process' | 'decision' | 'end' | 'input' | 'output' | 'database' | 'document' | 'delay' | 'manual';
  data: {
    label: string;
  };
  position: {
    x: number;
    y: number;
  };
}

interface FlowEdge {
  id: string;
  source: string;
  target: string;
  label?: string;
  animated?: boolean;
}

export interface FlowChartSpec {
  nodes: FlowNode[];
  edges: FlowEdge[];
  title: string;
  description: string;
}

const invokeRunAgent = async (agent: string, messages: { role: string; content: string }[]): Promise<string> => {
  const { data, error } = await supabase.functions.invoke('runAgent', {
    body: {
      agent,
      messages,
      temperature: 0.8,
      max_tokens: 10000,
      stream: false
    }
  });

  if (error) {
    throw new Error(error.message || 'Failed to fetch from runAgent');
  }

  return data.response || '';
};

/**
 * Builds a flowchart specification from user instruction
 */
export async function buildFlowChart(instruction: string): Promise<FlowChartSpec> {
  try {
    const messages = [
      {
        role: 'user',
        content: `Create a detailed flowchart for: ${instruction}

REQUIREMENTS:
- Use at least 5 different node types (input, output, database, document, decision, etc.)
- Include at least 1 decision node with branches
- Include at least 1 database OR document node
- Include at least 1 input OR output node
- DO NOT use only start-process-end pattern
- Make it visually diverse and contextually appropriate`
      }
    ];

    const content = await invokeRunAgent('flowchart', messages);

    if (!content) {
      throw new Error('No content in response');
    }

    // Extract JSON from response
    const jsonMatch = content.match(/\{[\s\S]*\}/);
    if (!jsonMatch) {
      throw new Error('Failed to parse flowchart JSON from response');
    }

    const flowSpec: FlowChartSpec = JSON.parse(jsonMatch[0]);

    // Validate the response
    if (!flowSpec.nodes || !Array.isArray(flowSpec.nodes) || flowSpec.nodes.length === 0) {
      throw new Error('Invalid flowchart specification: missing nodes');
    }

    if (!flowSpec.edges || !Array.isArray(flowSpec.edges)) {
      throw new Error('Invalid flowchart specification: missing edges');
    }

    return flowSpec;

  } catch (error) {
    console.error('Flow Agent Error:', error);

    // Return a simple fallback flowchart
    return {
      title: 'Error Creating Flowchart',
      description: error instanceof Error ? error.message : 'Failed to generate flowchart',
      nodes: [
        {
          id: 'node-1',
          type: 'start',
          data: { label: 'Start' },
          position: { x: 250, y: 0 }
        },
        {
          id: 'node-2',
          type: 'process',
          data: { label: 'Error occurred' },
          position: { x: 250, y: 150 }
        },
        {
          id: 'node-3',
          type: 'end',
          data: { label: 'End' },
          position: { x: 250, y: 300 }
        }
      ],
      edges: [
        {
          id: 'edge-1',
          source: 'node-1',
          target: 'node-2'
        },
        {
          id: 'edge-2',
          source: 'node-2',
          target: 'node-3'
        }
      ]
    };
  }
}

